/* OWN THE TREND — order hub
   Track an order, or manage it: edit delivery details, cancel, return, exchange.

   The page only renders. Every decision — is this the customer's order, can it
   still be cancelled, is it inside the return window — is made by the worker
   (cloudflare-worker/ott-orders-worker.js), which answers with the order and an
   `actions` block saying what is allowed and, if not, why. */
(function () {
  'use strict';

  /* ══ WORDING THAT ISN'T A THEME SETTING ═════════════════════════════════
     Anything in {braces} is filled in. */
  var TEXT = {
    lookupMissing: 'Enter your order number and the email or phone you used at checkout.',
    lookupNotFound: "We couldn't find an order with those details. Check the number, and use the same email or phone as at checkout.",
    lookupLimited: 'Too many attempts. Please wait a few minutes and try again.',
    lookupFailed: "Something went wrong on our side. Please try again in a moment.",
    notConfigured: 'Order lookup is not set up yet.',
    sessionExpired: 'For your security, please look up your order again.',
    searching: 'Finding your order…',
    otherOrder: 'Look up another order',

    placedOn: 'Placed {date}',
    expectedBy: 'Expected by {date}',
    deliveredOn: 'Delivered {date}',
    attempts: '{n} delivery attempts so far',
    courierNumber: 'Bosta tracking number',
    courierLink: 'Track on Bosta',
    copy: 'Copy',
    copied: 'Copied',
    deliverTo: 'Delivering to',
    deliveredTo: 'Delivered to',
    items: 'In this order',
    qty: 'Qty {n}',
    total: 'Total',
    otherShipments: 'Other shipments for this order',
    shipmentReturn: 'Return pickup',
    shipmentExchange: 'Exchange',
    shipmentRto: 'Coming back to us',
    manageCta: 'Need to change something?',
    manageLink: 'Manage this order',
    trackLink: 'Track this order',

    status: {
      processing: ["We're getting it ready", 'Your order is confirmed and being prepared. You can still change or cancel it until the courier collects it.'],
      shipped: ['On its way', 'Your order has left us and is with our courier, Bosta.'],
      out_for_delivery: ['Out for delivery', 'It arrives today. Keep your phone close — the courier calls before arriving.'],
      delivered: ['Delivered', 'Enjoy your new pair.'],
      attention: ["We couldn't deliver it yet", "The courier couldn't complete the delivery. They'll try again, or contact us to arrange a better time."],
      on_hold: ['On hold', "We've paused this delivery. Contact us if you didn't ask for this."],
      returning: ['Coming back to us', "This delivery wasn't completed and is on its way back to us. Contact us to arrange it again."],
      returned: ['Returned to us', "This delivery wasn't completed and has come back to us. Contact us to arrange it again."],
      cancelled: ['Cancelled', 'This order was cancelled {date}.']
    },

    // Why an option is switched off. The worker sends the key.
    reasons: {
      shipped: 'The courier has already collected your order, so it can no longer be changed. Once it arrives, you can return or exchange it.',
      delivered: 'Your order has been delivered.',
      cancelled: 'This order is cancelled.',
      not_delivered: 'Available once your order is delivered.',
      window_closed: 'This closed on {date}, 14 days after the order was placed.',
      already_requested: "We already have a request for this order — we'll be in touch.",
      nothing_left: 'There is nothing left on this order to return.',
      not_eligible: 'Not available for this order. Contact us and we will help.'
    },

    open: 'Start',
    until: 'Until {date}',
    back: 'Back',
    backToOrder: 'Back to your order',

    yourRequests: 'Your requests',
    requestType: { edit: 'Details updated', cancel: 'Cancellation', return: 'Return request', exchange: 'Exchange request' },

    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone number',
    province: 'Governorate',
    provinceOther: 'State / province',
    city: 'City / area',
    address1: 'Street and building',
    address2: 'Floor, apartment, landmark (optional)',
    saveDetails: 'Save changes',
    editSaved: ['Details updated', "We'll deliver to your new details."],
    editPending: ['Got it', "We've saved your new details and our team is passing them to the courier."],
    nothingChanged: "You haven't changed anything yet.",

    cancelReason: 'Why are you cancelling?',
    note: 'Anything else we should know? (optional)',
    cancelConfirm: 'I understand that cancelling cannot be undone.',
    cancelButton: 'Cancel my order',
    cancelDone: ['Your order is cancelled', "We've sent you a confirmation. If you paid online, the refund goes back to the same card."],
    cancelPending: ['Cancellation requested', "We've received your cancellation and will confirm by SMS shortly."],

    pickItems: 'Which items?',
    reason: 'Reason',
    swapFor: 'Swap for',
    swapSearch: 'Search our sunglasses',
    swapNone: 'No matches. Try another name or colour.',
    swapChange: 'Change',
    swapPickVariant: 'Choose an option',
    swapFallback: 'Or describe the model and colour you want',
    refundMethod: 'How should we refund you?',
    refundDetails: 'InstaPay address, wallet number or IBAN',
    policyLink: 'Read our returns policy',
    returnButton: 'Request return',
    exchangeButton: 'Request exchange',
    returnDone: ['Return requested', "Here's what happens next:"],
    exchangeDone: ['Exchange requested', "Here's what happens next:"],
    nextSteps: [
      'We confirm your request by SMS or a call within one working day.',
      'Bosta collects the pair from your address — have it packed in its original box.',
      'We check it, then send your {outcome}.'
    ],
    outcomeRefund: 'refund',
    outcomeExchange: 'new pair',

    working: 'One moment…',
    required: 'Please fill this in.',
    pickOne: 'Choose at least one item.',
    pickSwap: 'Tell us what you would like instead.',
    confirmFirst: 'Please confirm first.',
    invalidPhone: 'Enter a valid mobile number.',
    actionFailed: "That didn't go through. Please try again, or contact us.",
    notAllowed: "This can't be done on this order any more."
  };
  /* ══ NOTHING BELOW NEEDS EDITING ═════════════════════════════════════════ */

  // Shopify's codes for Egypt's governorates, which is what the order stores.
  var EG_PROVINCES = [
    ['C', 'Cairo'], ['GZ', 'Giza'], ['ALX', 'Alexandria'], ['SU', '6th of October'],
    ['HU', 'Helwan'], ['KB', 'Qalyubia'], ['SHR', 'Al Sharqia'], ['DK', 'Dakahlia'],
    ['GH', 'Gharbia'], ['MNF', 'Monufia'], ['BH', 'Beheira'], ['KFS', 'Kafr el-Sheikh'],
    ['DT', 'Damietta'], ['PTS', 'Port Said'], ['IS', 'Ismailia'], ['SUZ', 'Suez'],
    ['FYM', 'Faiyum'], ['BNS', 'Beni Suef'], ['MN', 'Minya'], ['AST', 'Asyut'],
    ['SHG', 'Sohag'], ['KN', 'Qena'], ['LX', 'Luxor'], ['ASN', 'Aswan'],
    ['BA', 'Red Sea'], ['MT', 'Matrouh'], ['WAD', 'New Valley'], ['JS', 'South Sinai'],
    ['SIN', 'North Sinai']
  ];

  var STEP_KEYS = ['placed', 'shipped', 'transit', 'out', 'delivered'];
  var STORE_KEY = 'ottoh_session_v1';

  var ICONS = {
    edit: '<path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4"/>',
    cancel: '<circle cx="12" cy="12" r="8.5"/><path d="M9 9l6 6M15 9l-6 6"/>',
    return: '<path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 010 12h-3"/>',
    exchange: '<path d="M7 4L3 8l4 4"/><path d="M3 8h13"/><path d="M17 20l4-4-4-4"/><path d="M21 16H8"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    back: '<path d="M19 12H5M11 18l-6-6 6-6"/>',
    external: '<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5"/>'
  };

  function icon(name, size) {
    return '<svg viewBox="0 0 24 24" width="' + (size || 20) + '" height="' + (size || 20) +
      '" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      ICONS[name] + '</svg>';
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fill(text, vars) {
    return String(text).replace(/\{(\w+)\}/g, function (m, k) {
      return vars && vars[k] != null ? vars[k] : m;
    });
  }

  function lines(text) {
    return String(text || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
  }

  function locale() {
    return document.documentElement.lang || 'en';
  }

  function fmtDate(iso, withTime) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var opts = { weekday: 'short', day: 'numeric', month: 'short' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    if (withTime) { opts.hour = 'numeric'; opts.minute = '2-digit'; }
    try { return new Intl.DateTimeFormat(locale(), opts).format(d); } catch (e) { return d.toDateString(); }
  }

  function fmtMoney(money) {
    if (!money || money.amount == null) return '';
    var n = Number(money.amount);
    try {
      return new Intl.NumberFormat(locale(), {
        style: 'currency',
        currency: money.currency || 'EGP',
        maximumFractionDigits: n % 1 ? 2 : 0
      }).format(n);
    } catch (e) {
      return (money.currency || '') + ' ' + n.toFixed(2);
    }
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function storage() {
    try { return window.sessionStorage; } catch (e) { return null; }
  }

  function readSession() {
    var s = storage();
    if (!s) return null;
    try { return JSON.parse(s.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }

  function writeSession(value) {
    var s = storage();
    if (!s) return;
    try {
      if (value) s.setItem(STORE_KEY, JSON.stringify(value));
      else s.removeItem(STORE_KEY);
    } catch (e) { /* private mode — the page still works, it just forgets on reload */ }
  }

  /* ════════════════════════════════════════════════════════════════════════ */

  function Hub(root) {
    this.root = root;
    try {
      this.cfg = JSON.parse(root.querySelector('[data-ottoh-config]').textContent);
    } catch (e) {
      this.cfg = {};
    }
    this.copy = this.cfg.copy || {};
    this.worker = String(this.cfg.worker || '').trim().replace(/\/+$/, '');
    this.order = null;
    this.token = null;
    this.panel = null; // which manage form is open, if any

    this.form = root.querySelector('[data-ottoh-lookup]');
    this.orderInput = root.querySelector('[data-ottoh-order]');
    this.contactInput = root.querySelector('[data-ottoh-contact]');
    this.lookupButton = root.querySelector('[data-ottoh-lookup-submit]');
    this.lookupMessage = root.querySelector('[data-ottoh-lookup-message]');
    this.result = root.querySelector('[data-ottoh-result]');

    var params = new URLSearchParams(window.location.search);
    var view = params.get('view');
    this.view = view === 'manage' || view === 'track' ? view : this.cfg.defaultView || 'track';
    if (params.get('order')) this.orderInput.value = params.get('order').replace(/[^\w-]/g, '');

    this.bind();
    this.setView(this.view, true);
    this.resume();
  }

  Hub.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-ottoh-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () { self.setView(tab.getAttribute('data-ottoh-tab')); });
      tab.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var next = tab.getAttribute('data-ottoh-tab') === 'track' ? 'manage' : 'track';
        self.setView(next);
        self.root.querySelector('[data-ottoh-tab="' + next + '"]').focus();
      });
    });

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.lookup();
    });

    // One delegated listener for everything the result area renders.
    this.result.addEventListener('click', function (e) {
      var el = e.target.closest('[data-act]');
      if (!el || !self.result.contains(el)) return;
      var act = el.getAttribute('data-act');
      if (act === 'view') self.setView(el.getAttribute('data-to'));
      else if (act === 'open') self.openPanel(el.getAttribute('data-panel'));
      else if (act === 'close') self.openPanel(null);
      else if (act === 'reset') self.reset();
      else if (act === 'copy') self.copyText(el);
    });
  };

  /* ---- views ------------------------------------------------------------ */

  Hub.prototype.setView = function (view, initial) {
    this.view = view;
    this.root.querySelectorAll('[data-ottoh-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-ottoh-tab') === view;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.root.querySelectorAll('[data-ottoh-intro]').forEach(function (p) {
      p.hidden = p.getAttribute('data-ottoh-intro') !== view;
    });

    if (!initial) {
      // A real URL per view, so either can be bookmarked or linked to.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('view', view);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* old browser: the view still switches */ }
      this.panel = null;
    }

    var session = readSession();
    if (session) { session.view = view; writeSession(session); }
    if (this.order) this.render();
  };

  /* ---- talking to the worker ------------------------------------------- */

  Hub.prototype.api = function (path, body) {
    var self = this;
    if (!this.worker) return Promise.resolve({ ok: false, error: 'config' });

    return fetch(this.worker + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (res) {
      return res.json().catch(function () { return { ok: false, error: 'server' }; });
    }, function () {
      return { ok: false, error: 'network' };
    }).then(function (data) {
      if (data && data.error === 'session') self.expire();
      return data || { ok: false, error: 'server' };
    });
  };

  Hub.prototype.lookup = function () {
    var self = this;
    var order = this.orderInput.value.trim();
    var contact = this.contactInput.value.trim();
    var trap = this.form.querySelector('[name="company"]');

    if (!order || !contact) return this.lookupError(TEXT.lookupMissing);
    if (!this.worker) return this.lookupError(TEXT.notConfigured);

    this.busy(this.lookupButton, true, TEXT.searching);
    this.lookupMessage.hidden = true;

    this.api('/lookup', {
      order: order,
      contact: contact,
      phonePrefix: this.cfg.phonePrefix,
      company: trap ? trap.value : ''
    }).then(function (data) {
      self.busy(self.lookupButton, false);
      if (!data.ok) {
        var msg = data.error === 'not_found' ? TEXT.lookupNotFound
          : data.error === 'rate_limited' ? TEXT.lookupLimited
          : data.error === 'config' ? TEXT.notConfigured
          : TEXT.lookupFailed;
        return self.lookupError(msg);
      }
      self.accept(data, true);
    });
  };

  Hub.prototype.lookupError = function (msg) {
    this.lookupMessage.textContent = msg;
    this.lookupMessage.hidden = false;
  };

  Hub.prototype.accept = function (data, focus) {
    this.token = data.token || this.token;
    this.order = data.order;
    writeSession({ token: this.token, view: this.view });
    this.form.hidden = true;
    this.result.hidden = false;
    this.render();
    if (focus) {
      this.result.focus({ preventScroll: true });
      this.scrollTo(this.root);
    }
  };

  /* A reload keeps the order open for as long as the worker's token lives. */
  Hub.prototype.resume = function () {
    var self = this;
    var session = readSession();
    if (!session || !session.token || !this.worker) return;

    this.token = session.token;
    this.form.hidden = true;
    this.result.hidden = false;
    this.result.innerHTML = '<p class="ottoh-loading">' + esc(TEXT.searching) + '</p>';

    this.api('/order', { token: this.token }).then(function (data) {
      if (data.ok) self.accept(data, false);
      else self.reset(data.error === 'session' ? TEXT.sessionExpired : null);
    });
  };

  Hub.prototype.refresh = function () {
    var self = this;
    return this.api('/order', { token: this.token }).then(function (data) {
      if (data.ok) { self.order = data.order; self.token = data.token || self.token; }
      return data;
    });
  };

  Hub.prototype.expire = function () {
    this.reset(TEXT.sessionExpired);
  };

  Hub.prototype.reset = function (message) {
    writeSession(null);
    this.order = null;
    this.token = null;
    this.panel = null;
    this.result.hidden = true;
    this.result.innerHTML = '';
    this.form.hidden = false;
    if (message) this.lookupError(message);
    else this.lookupMessage.hidden = true;
    this.orderInput.focus();
  };

  /* ---- rendering --------------------------------------------------------- */

  Hub.prototype.render = function () {
    if (!this.order) return;
    var o = this.order;
    var html = this.renderSummaryBar(o);

    if (this.view === 'track') html += this.renderTrack(o);
    else if (this.panel) html += this.renderPanel(this.panel, o);
    else html += this.renderManage(o);

    this.result.innerHTML = html;
    if (this.view === 'manage' && this.panel) this.wirePanel(this.panel);
  };

  Hub.prototype.renderSummaryBar = function (o) {
    return '<div class="ottoh-bar">' +
      '<div class="ottoh-bar__id"><strong>' + esc(o.name) + '</strong>' +
        '<span>' + esc(fill(TEXT.placedOn, { date: fmtDate(o.placedAt) })) + '</span></div>' +
      '<button type="button" class="ottoh-link" data-act="reset">' + esc(TEXT.otherOrder) + '</button>' +
    '</div>';
  };

  Hub.prototype.statusCopy = function (o) {
    var pair = TEXT.status[o.status] || TEXT.status.processing;
    return {
      title: pair[0],
      body: fill(pair[1], { date: fmtDate(o.cancelledAt) })
    };
  };

  Hub.prototype.renderTrack = function (o) {
    var c = this.copy;
    var st = this.statusCopy(o);
    var t = o.tracking || {};
    var meta = [];

    if (o.status === 'delivered' && t.deliveredAt) meta.push(fill(TEXT.deliveredOn, { date: fmtDate(t.deliveredAt, true) }));
    else if (t.promisedAt && o.status !== 'cancelled' && o.status !== 'returned') meta.push(fill(TEXT.expectedBy, { date: fmtDate(t.promisedAt) }));
    if (t.attempts > 1 && o.status !== 'delivered') meta.push(fill(TEXT.attempts, { n: t.attempts }));

    var html = '<div class="ottoh-status ottoh-status--' + esc(o.status) + '">' +
      '<h2 class="ottoh-status__title">' + esc(st.title) + '</h2>' +
      '<p class="ottoh-status__body">' + esc(o.statusNote || st.body) + '</p>' +
      (meta.length ? '<p class="ottoh-status__meta">' + meta.map(esc).join(' · ') + '</p>' : '') +
    '</div>';

    if (o.status !== 'cancelled') {
      var labels = [c.stepPlaced, c.stepShipped, c.stepTransit, c.stepOut, c.stepDelivered];
      var steps = t.steps && t.steps.length ? t.steps : [{ key: 'placed', done: true, at: o.placedAt }];
      var byKey = {};
      steps.forEach(function (s) { byKey[s.key] = s; });
      var current = -1;
      STEP_KEYS.forEach(function (k, i) { if (byKey[k] && byKey[k].done) current = i; });
      var stalled = o.status === 'attention' || o.status === 'returning' || o.status === 'returned' || o.status === 'on_hold';

      html += '<ol class="ottoh-steps' + (stalled ? ' ottoh-steps--stalled' : '') + '" style="--ottoh-progress:' + Math.max(0, current) / (STEP_KEYS.length - 1) + '">';
      STEP_KEYS.forEach(function (k, i) {
        var s = byKey[k] || {};
        var state = i < current ? 'done' : i === current ? 'current' : 'todo';
        html += '<li class="ottoh-step ottoh-step--' + state + '"' + (state === 'current' ? ' aria-current="step"' : '') + '>' +
          '<span class="ottoh-step__dot">' + (state !== 'todo' ? icon('check', 14) : '') + '</span>' +
          '<span class="ottoh-step__label">' + esc(labels[i] || k) + '</span>' +
          '<span class="ottoh-step__date">' + esc(s.done && s.at ? fmtDate(s.at, k !== 'placed') : '') + '</span>' +
        '</li>';
      });
      html += '</ol>';
    }

    if (t.number) {
      html += '<div class="ottoh-courier">' +
        '<div><span class="ottoh-courier__label">' + esc(TEXT.courierNumber) + '</span>' +
        '<span class="ottoh-courier__number">' + esc(t.number) + '</span></div>' +
        '<div class="ottoh-courier__actions">' +
          '<button type="button" class="ottoh-link" data-act="copy" data-value="' + esc(t.number) + '">' + esc(TEXT.copy) + '</button>' +
          (t.url ? '<a class="ottoh-link" href="' + esc(t.url) + '" target="_blank" rel="noopener">' + esc(TEXT.courierLink) + ' ' + icon('external', 14) + '</a>' : '') +
        '</div>' +
      '</div>';
    }

    if (o.shipments && o.shipments.length) {
      html += '<div class="ottoh-block"><h3 class="ottoh-block__title">' + esc(TEXT.otherShipments) + '</h3><ul class="ottoh-shipments">';
      o.shipments.forEach(function (s) {
        var kind = s.kind === 'return' ? TEXT.shipmentReturn : s.kind === 'exchange' ? TEXT.shipmentExchange : TEXT.shipmentRto;
        html += '<li><span><strong>' + esc(kind) + '</strong> · ' + esc(s.state || '') + (s.at ? ' · ' + esc(fmtDate(s.at)) : '') + '</span>' +
          (s.url ? '<a class="ottoh-link" href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.number) + ' ' + icon('external', 14) + '</a>' : '<span>' + esc(s.number) + '</span>') +
        '</li>';
      });
      html += '</ul></div>';
    }

    html += this.renderItems(o);
    html += this.renderAddress(o);

    if (o.status !== 'cancelled') {
      html += '<div class="ottoh-cta"><span>' + esc(TEXT.manageCta) + '</span>' +
        '<button type="button" class="ottoh-link ottoh-link--strong" data-act="view" data-to="manage">' + esc(TEXT.manageLink) + ' ' + icon('arrow', 16) + '</button></div>';
    }
    return html;
  };

  Hub.prototype.renderItems = function (o) {
    var html = '<div class="ottoh-block"><h3 class="ottoh-block__title">' + esc(TEXT.items) + '</h3><ul class="ottoh-items">';
    (o.items || []).forEach(function (it) {
      html += '<li class="ottoh-item">' + thumb(it) +
        '<div class="ottoh-item__text"><span class="ottoh-item__title">' + esc(it.title) + '</span>' +
        (it.variant ? '<span class="ottoh-item__variant">' + esc(it.variant) + '</span>' : '') +
        '<span class="ottoh-item__qty">' + esc(fill(TEXT.qty, { n: it.qty })) + '</span></div>' +
        '<span class="ottoh-item__price">' + esc(fmtMoney(it.price)) + '</span>' +
      '</li>';
    });
    html += '</ul>';
    if (o.total) {
      html += '<p class="ottoh-total"><span>' + esc(TEXT.total) + (o.paymentLabel ? ' · ' + esc(o.paymentLabel) : '') + '</span>' +
        '<strong>' + esc(fmtMoney(o.total)) + '</strong></p>';
    }
    return html + '</div>';
  };

  Hub.prototype.renderAddress = function (o) {
    var a = o.shipping;
    if (!a) return '';
    var name = [a.firstName, a.lastName].filter(Boolean).join(' ');
    var place = [a.address1, a.address2, a.city, a.province].filter(Boolean).join(', ');
    return '<div class="ottoh-block ottoh-address"><h3 class="ottoh-block__title">' +
      esc(o.status === 'delivered' ? TEXT.deliveredTo : TEXT.deliverTo) + '</h3>' +
      '<p>' + esc(name) + (a.phone ? '<br>' + esc(a.phone) : '') + '<br>' + esc(place) + '</p></div>';
  };

  Hub.prototype.renderManage = function (o) {
    var c = this.copy;
    var actions = o.actions || {};
    var html = '';

    if (o.requests && o.requests.length) {
      html += '<div class="ottoh-notice"><strong>' + esc(TEXT.yourRequests) + '</strong><ul>';
      o.requests.forEach(function (r) {
        html += '<li>' + esc(TEXT.requestType[r.type] || r.type) + ' · ' + esc(fmtDate(r.at, true)) + '</li>';
      });
      html += '</ul></div>';
    }

    var cards = [
      ['edit', 'edit', c.editTitle, c.editText],
      ['cancel', 'cancel', c.cancelTitle, c.cancelText],
      ['return', 'return', c.returnTitle, c.returnText],
      ['exchange', 'exchange', c.exchangeTitle, c.exchangeText]
    ];

    html += '<div class="ottoh-options">';
    cards.forEach(function (card) {
      var a = actions[card[0]] || { allowed: false, reason: 'not_eligible' };
      var reason = a.reason === 'shipped' && o.status === 'delivered' ? 'delivered' : a.reason;
      var why = a.allowed ? (a.until ? fill(TEXT.until, { date: fmtDate(a.until) }) : '')
        : fill(TEXT.reasons[reason] || TEXT.reasons.not_eligible, { date: fmtDate(a.until) });

      html += '<div class="ottoh-option' + (a.allowed ? '' : ' is-off') + (card[0] === 'cancel' ? ' ottoh-option--danger' : '') + '">' +
        '<span class="ottoh-option__icon">' + icon(card[1], 22) + '</span>' +
        '<div class="ottoh-option__text"><h3>' + esc(card[2]) + '</h3><p>' + esc(card[3]) + '</p>' +
        (why ? '<p class="ottoh-option__why">' + esc(why) + '</p>' : '') + '</div>' +
        (a.allowed ? '<button type="button" class="ottoh-btn ottoh-btn--small" data-act="open" data-panel="' + card[0] + '">' + esc(TEXT.open) + '</button>' : '') +
      '</div>';
    });
    html += '</div>';

    html += '<div class="ottoh-cta"><span></span><button type="button" class="ottoh-link ottoh-link--strong" data-act="view" data-to="track">' +
      esc(TEXT.trackLink) + ' ' + icon('arrow', 16) + '</button></div>';
    return html;
  };

  function thumb(it) {
    return it.image
      ? '<img class="ottoh-item__img" src="' + esc(it.image) + '" alt="" width="64" height="64" loading="lazy">'
      : '<span class="ottoh-item__img ottoh-item__img--empty"></span>';
  }

  /* ---- manage panels ------------------------------------------------------ */

  Hub.prototype.openPanel = function (name) {
    this.panel = name;
    this.render();
    this.scrollTo(this.result);
    var first = this.result.querySelector('input:not([type=hidden]):not([type=checkbox]), select, textarea');
    if (first) first.focus({ preventScroll: true });
  };

  Hub.prototype.renderPanel = function (name, o) {
    var c = this.copy;
    var title = { edit: c.editTitle, cancel: c.cancelTitle, return: c.returnTitle, exchange: c.exchangeTitle }[name];
    var body = name === 'edit' ? this.renderEdit(o)
      : name === 'cancel' ? this.renderCancel(o)
      : this.renderReturn(o, name);

    return '<div class="ottoh-panel">' +
      '<button type="button" class="ottoh-link ottoh-panel__back" data-act="close">' + icon('back', 16) + ' ' + esc(TEXT.back) + '</button>' +
      '<h2 class="ottoh-panel__title">' + esc(title) + '</h2>' +
      '<form class="ottoh-form" data-ottoh-form="' + name + '" novalidate>' + body +
        '<p class="ottoh-form__error" data-form-error role="alert" hidden></p>' +
      '</form></div>';
  };

  function field(name, label, value, opts) {
    opts = opts || {};
    return '<div class="ottoh-field' + (opts.half ? ' ottoh-field--half' : '') + '">' +
      '<label class="ottoh-field__label" for="ottoh-f-' + name + '">' + esc(label) + '</label>' +
      '<input class="ottoh-field__input" id="ottoh-f-' + name + '" name="' + name + '" type="' + (opts.type || 'text') + '"' +
      ' value="' + esc(value || '') + '"' + (opts.required ? ' required' : '') +
      (opts.autocomplete ? ' autocomplete="' + opts.autocomplete + '"' : '') +
      (opts.inputmode ? ' inputmode="' + opts.inputmode + '"' : '') +
      (opts.maxlength ? ' maxlength="' + opts.maxlength + '"' : '') + '>' +
    '</div>';
  }

  function selectField(name, label, options, value, opts) {
    opts = opts || {};
    var html = '<div class="ottoh-field' + (opts.half ? ' ottoh-field--half' : '') + '">' +
      '<label class="ottoh-field__label" for="ottoh-f-' + name + '">' + esc(label) + '</label>' +
      '<select class="ottoh-field__input ottoh-field__select" id="ottoh-f-' + name + '" name="' + name + '"' + (opts.required ? ' required' : '') + '>' +
      '<option value="">—</option>';
    options.forEach(function (o) {
      var v = Array.isArray(o) ? o[0] : o;
      var l = Array.isArray(o) ? o[1] : o;
      html += '<option value="' + esc(v) + '"' + (String(v) === String(value) ? ' selected' : '') + '>' + esc(l) + '</option>';
    });
    return html + '</select></div>';
  }

  function textarea(name, label) {
    return '<div class="ottoh-field"><label class="ottoh-field__label" for="ottoh-f-' + name + '">' + esc(label) + '</label>' +
      '<textarea class="ottoh-field__input" id="ottoh-f-' + name + '" name="' + name + '" rows="3" maxlength="500"></textarea></div>';
  }

  function checkbox(name, label) {
    return '<label class="ottoh-check"><input type="checkbox" name="' + name + '"><span>' + esc(label) + '</span></label>';
  }

  function submit(label, danger) {
    return '<button type="submit" class="ottoh-btn ottoh-btn--primary' + (danger ? ' ottoh-btn--danger' : '') + '">' + esc(label) + '</button>';
  }

  Hub.prototype.renderEdit = function (o) {
    var a = o.shipping || {};
    var egypt = !a.countryCode || a.countryCode === 'EG';
    return '<div class="ottoh-grid">' +
      field('firstName', TEXT.firstName, a.firstName, { half: true, required: true, autocomplete: 'given-name', maxlength: 60 }) +
      field('lastName', TEXT.lastName, a.lastName, { half: true, required: true, autocomplete: 'family-name', maxlength: 60 }) +
      field('phone', TEXT.phone, a.phone, { type: 'tel', required: true, autocomplete: 'tel', inputmode: 'tel', maxlength: 20 }) +
      (egypt
        ? selectField('provinceCode', TEXT.province, EG_PROVINCES, a.provinceCode, { half: true, required: true })
        : field('province', TEXT.provinceOther, a.province, { half: true, maxlength: 60 })) +
      field('city', TEXT.city, a.city, { half: true, required: true, autocomplete: 'address-level2', maxlength: 60 }) +
      field('address1', TEXT.address1, a.address1, { required: true, autocomplete: 'address-line1', maxlength: 120 }) +
      field('address2', TEXT.address2, a.address2, { autocomplete: 'address-line2', maxlength: 120 }) +
    '</div>' + submit(TEXT.saveDetails);
  };

  Hub.prototype.renderCancel = function () {
    var reasons = lines(this.cfg.cancelReasons);
    var html = '<fieldset class="ottoh-fieldset"><legend class="ottoh-field__label">' + esc(TEXT.cancelReason) + '</legend><div class="ottoh-radios">';
    reasons.forEach(function (r, i) {
      html += '<label class="ottoh-radio"><input type="radio" name="reason" value="' + esc(r) + '"' + (i === 0 ? ' required' : '') + '><span>' + esc(r) + '</span></label>';
    });
    html += '</div></fieldset>';
    return html + textarea('note', TEXT.note) + checkbox('confirm', TEXT.cancelConfirm) + submit(TEXT.cancelButton, true);
  };

  Hub.prototype.renderReturn = function (o, kind) {
    var c = this.copy;
    var a = (o.actions || {})[kind] || {};
    var reasons = lines(this.cfg.returnReasons);
    var html = '<fieldset class="ottoh-fieldset"><legend class="ottoh-field__label">' + esc(TEXT.pickItems) + '</legend><div class="ottoh-pick">';

    (a.items || []).forEach(function (it, i) {
      var qtyOptions = [];
      for (var n = 1; n <= it.qty; n++) qtyOptions.push([n, String(n)]);
      html += '<div class="ottoh-pick__item" data-item="' + esc(it.id) + '">' +
        '<label class="ottoh-pick__head"><input type="checkbox" name="item" value="' + esc(it.id) + '"' + ((a.items.length === 1) ? ' checked' : '') + '>' +
          thumb(it) +
          '<span class="ottoh-item__text"><span class="ottoh-item__title">' + esc(it.title) + '</span>' +
          (it.variant ? '<span class="ottoh-item__variant">' + esc(it.variant) + '</span>' : '') + '</span></label>' +
        '<div class="ottoh-pick__more" data-more' + (a.items.length === 1 ? '' : ' hidden') + '>' +
          '<div class="ottoh-grid">' +
            (it.qty > 1 ? selectField('qty-' + i, TEXT.qty.replace(' {n}', ''), qtyOptions, it.qty, { half: true }) : '') +
            selectField('reason-' + i, TEXT.reason, reasons, '', { half: it.qty > 1, required: true }) +
          '</div>' +
          (kind === 'exchange' ? swapPicker(i) : '') +
        '</div>' +
      '</div>';
    });
    html += '</div></fieldset>';

    if (kind === 'return') {
      html += '<div class="ottoh-grid">' +
        selectField('refundMethod', TEXT.refundMethod, lines(this.cfg.refundMethods), '', { half: true, required: true }) +
        field('refundDetails', TEXT.refundDetails, '', { half: true, maxlength: 80 }) +
      '</div>';
      if (c.refundNote) html += '<p class="ottoh-form__note">' + esc(c.refundNote) + '</p>';
    } else if (c.exchangeNote) {
      html += '<p class="ottoh-form__note">' + esc(c.exchangeNote) + '</p>';
    }

    html += textarea('note', TEXT.note);
    if (c.returnConditions) html += checkbox('confirm', c.returnConditions);
    if (this.cfg.policyUrl) html += '<p class="ottoh-form__note"><a class="ottoh-link" href="' + esc(this.cfg.policyUrl) + '" target="_blank" rel="noopener">' + esc(TEXT.policyLink) + ' ' + icon('external', 12) + '</a></p>';
    return html + submit(kind === 'return' ? TEXT.returnButton : TEXT.exchangeButton);
  };

  function swapPicker(i) {
    return '<div class="ottoh-swap" data-swap="' + i + '">' +
      '<label class="ottoh-field__label" for="ottoh-swap-' + i + '">' + esc(TEXT.swapFor) + '</label>' +
      '<div class="ottoh-swap__chosen" data-swap-chosen hidden></div>' +
      '<div data-swap-search>' +
        '<input class="ottoh-field__input" id="ottoh-swap-' + i + '" type="search" autocomplete="off" placeholder="' + esc(TEXT.swapSearch) + '" data-swap-input>' +
        '<div class="ottoh-swap__results" data-swap-results role="listbox"></div>' +
        '<input class="ottoh-field__input ottoh-swap__fallback" type="text" maxlength="120" placeholder="' + esc(TEXT.swapFallback) + '" data-swap-text>' +
      '</div>' +
    '</div>';
  }

  /* ---- panel behaviour --------------------------------------------------- */

  Hub.prototype.wirePanel = function (name) {
    var self = this;
    var form = this.result.querySelector('[data-ottoh-form]');
    if (!form) return;

    if (name === 'return' || name === 'exchange') {
      form.querySelectorAll('.ottoh-pick__item').forEach(function (row) {
        var box = row.querySelector('input[name="item"]');
        var more = row.querySelector('[data-more]');
        box.addEventListener('change', function () { more.hidden = !box.checked; });
        var swap = row.querySelector('[data-swap]');
        if (swap) self.wireSwap(swap);
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (name === 'edit') self.submitEdit(form);
      else if (name === 'cancel') self.submitCancel(form);
      else self.submitReturn(form, name);
    });
  };

  /* Search the store's own catalogue for the pair they want instead. Uses the
     storefront's predictive search, then the product's .js for its variants. */
  Hub.prototype.wireSwap = function (box) {
    var input = box.querySelector('[data-swap-input]');
    var results = box.querySelector('[data-swap-results]');
    var chosen = box.querySelector('[data-swap-chosen]');
    var search = box.querySelector('[data-swap-search]');
    var timer = null;
    var seq = 0;

    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = window.setTimeout(function () {
        var mine = ++seq;
        var url = routeRoot() + 'search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide';
        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (mine !== seq) return;
            var products = (((data || {}).resources || {}).results || {}).products || [];
            if (!products.length) { results.innerHTML = '<p class="ottoh-swap__empty">' + esc(TEXT.swapNone) + '</p>'; return; }
            results.innerHTML = products.map(function (p) {
              var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
              return '<button type="button" class="ottoh-swap__result" role="option" data-handle="' + esc(p.handle) + '" data-title="' + esc(p.title) + '">' +
                (img ? '<img src="' + esc(sized(img, 96)) + '" alt="" width="40" height="40">' : '') +
                '<span>' + esc(p.title) + '</span></button>';
            }).join('');
          })
          .catch(function () { results.innerHTML = ''; });
      }, 250);
    });

    results.addEventListener('click', function (e) {
      var btn = e.target.closest('.ottoh-swap__result');
      if (!btn) return;
      var handle = btn.getAttribute('data-handle');
      var title = btn.getAttribute('data-title');
      fetch(routeRoot() + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          var variants = (p.variants || []).filter(function (v) { return v.available; });
          choose(title, handle, variants.length ? variants : p.variants || [], p.featured_image);
        })
        .catch(function () { choose(title, handle, [], null); });
    });

    function choose(title, handle, variants, image) {
      box._swap = { product: title, handle: handle, variantId: variants.length === 1 ? variants[0].id : null, variant: variants.length === 1 ? variants[0].title : '' };
      var html = (image ? '<img src="' + esc(sized(image, 96)) + '" alt="" width="40" height="40">' : '') +
        '<span class="ottoh-swap__name">' + esc(title) + '</span>';
      if (variants.length > 1) {
        html += '<select class="ottoh-field__input ottoh-field__select" data-swap-variant aria-label="' + esc(TEXT.swapPickVariant) + '"><option value="">' + esc(TEXT.swapPickVariant) + '</option>' +
          variants.map(function (v) { return '<option value="' + v.id + '">' + esc(v.title) + '</option>'; }).join('') + '</select>';
      }
      html += '<button type="button" class="ottoh-link" data-swap-reset>' + esc(TEXT.swapChange) + '</button>';
      chosen.innerHTML = html;
      chosen.hidden = false;
      search.hidden = true;

      var sel = chosen.querySelector('[data-swap-variant]');
      if (sel) sel.addEventListener('change', function () {
        box._swap.variantId = sel.value || null;
        box._swap.variant = sel.value ? sel.options[sel.selectedIndex].text : '';
      });
      chosen.querySelector('[data-swap-reset]').addEventListener('click', function () {
        box._swap = null;
        chosen.hidden = true;
        chosen.innerHTML = '';
        search.hidden = false;
        input.focus();
      });
    }
  };

  function sized(url, width) {
    url = String(url || '');
    if (url.indexOf('//') === 0) url = 'https:' + url;
    url = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ---- submitting ------------------------------------------------------- */

  Hub.prototype.formError = function (form, msg, el) {
    var box = form.querySelector('[data-form-error]');
    box.textContent = msg;
    box.hidden = false;
    form.querySelectorAll('.is-invalid').forEach(function (x) { x.classList.remove('is-invalid'); });
    if (el) {
      el.classList.add('is-invalid');
      el.focus();
    }
    return false;
  };

  Hub.prototype.requireFields = function (form) {
    var bad = null;
    form.querySelectorAll('[required]').forEach(function (el) {
      if (bad || el.type === 'radio' || el.closest('[hidden]')) return;
      if (!String(el.value || '').trim()) bad = el;
    });
    if (bad) return this.formError(form, TEXT.required, bad);
    return true;
  };

  Hub.prototype.send = function (form, path, body, onDone) {
    var self = this;
    var btn = form.querySelector('[type="submit"]');
    form.querySelector('[data-form-error]').hidden = true;
    this.busy(btn, true, TEXT.working);

    body.token = this.token;
    this.api(path, body).then(function (data) {
      self.busy(btn, false);
      if (data.error === 'session') return;
      if (!data.ok) {
        var msg = data.error === 'not_allowed' ? TEXT.notAllowed
          : data.error === 'invalid_phone' ? TEXT.invalidPhone
          : data.error === 'unchanged' ? TEXT.nothingChanged
          : data.message || TEXT.actionFailed;
        return self.formError(form, msg, data.field ? form.querySelector('[name="' + data.field + '"]') : null);
      }
      if (data.order) self.order = data.order;
      onDone(data);
    });
  };

  Hub.prototype.submitEdit = function (form) {
    if (!this.requireFields(form)) return;
    var get = function (n) { var el = form.querySelector('[name="' + n + '"]'); return el ? el.value.trim() : ''; };
    var phoneDigits = get('phone').replace(/\D/g, '');
    if (phoneDigits.length < 8) return this.formError(form, TEXT.invalidPhone, form.querySelector('[name="phone"]'));

    var provinceSelect = form.querySelector('[name="provinceCode"]');
    var self = this;
    this.send(form, '/edit', {
      firstName: get('firstName'),
      lastName: get('lastName'),
      phone: get('phone'),
      phonePrefix: this.cfg.phonePrefix,
      provinceCode: get('provinceCode'),
      province: provinceSelect ? provinceSelect.options[provinceSelect.selectedIndex].text : get('province'),
      city: get('city'),
      address1: get('address1'),
      address2: get('address2')
    }, function (data) {
      self.done(data.pending ? TEXT.editPending : TEXT.editSaved);
    });
  };

  Hub.prototype.submitCancel = function (form) {
    var reason = form.querySelector('[name="reason"]:checked');
    if (!reason) return this.formError(form, TEXT.required, form.querySelector('[name="reason"]'));
    var confirm = form.querySelector('[name="confirm"]');
    if (!confirm.checked) return this.formError(form, TEXT.confirmFirst, confirm);

    var self = this;
    this.send(form, '/cancel', {
      reason: reason.value,
      note: form.querySelector('[name="note"]').value.trim()
    }, function (data) {
      self.done(data.pending ? TEXT.cancelPending : TEXT.cancelDone);
    });
  };

  Hub.prototype.submitReturn = function (form, kind) {
    var self = this;
    var rows = [];
    var problem = null;

    form.querySelectorAll('.ottoh-pick__item').forEach(function (row, i) {
      if (problem) return;
      var box = row.querySelector('input[name="item"]');
      if (!box.checked) return;
      var qtyEl = row.querySelector('[name="qty-' + i + '"]');
      var reasonEl = row.querySelector('[name="reason-' + i + '"]');
      if (!reasonEl.value) { problem = [TEXT.required, reasonEl]; return; }

      var item = { id: box.value, qty: qtyEl ? Number(qtyEl.value) || 1 : 1, reason: reasonEl.value };
      if (kind === 'exchange') {
        var swap = row.querySelector('[data-swap]');
        var picked = swap._swap;
        var typed = swap.querySelector('[data-swap-text]').value.trim();
        if (picked && !picked.variantId && swap.querySelector('[data-swap-variant]')) { problem = [TEXT.swapPickVariant, swap.querySelector('[data-swap-variant]')]; return; }
        if (!picked && !typed) { problem = [TEXT.pickSwap, swap.querySelector('[data-swap-input]')]; return; }
        item.swap = picked
          ? { product: picked.product, variant: picked.variant, variantId: picked.variantId, handle: picked.handle }
          : { text: typed };
      }
      rows.push(item);
    });

    if (problem) return this.formError(form, problem[0], problem[1]);
    if (!rows.length) return this.formError(form, TEXT.pickOne, form.querySelector('input[name="item"]'));

    var method = form.querySelector('[name="refundMethod"]');
    if (method && !method.value) return this.formError(form, TEXT.required, method);
    var confirm = form.querySelector('[name="confirm"]');
    if (confirm && !confirm.checked) return this.formError(form, TEXT.confirmFirst, confirm);

    var details = form.querySelector('[name="refundDetails"]');
    this.send(form, '/return', {
      kind: kind,
      items: rows,
      refundMethod: method ? method.value : '',
      refundDetails: details ? details.value.trim() : '',
      note: form.querySelector('[name="note"]').value.trim()
    }, function () {
      var steps = TEXT.nextSteps.map(function (s) {
        return fill(s, { outcome: kind === 'return' ? TEXT.outcomeRefund : TEXT.outcomeExchange });
      });
      self.done(kind === 'return' ? TEXT.returnDone : TEXT.exchangeDone, steps);
    });
  };

  /* A confirmation in place of the form, then back to the (refreshed) order. */
  Hub.prototype.done = function (pair, steps) {
    var self = this;
    var html = '<div class="ottoh-done">' +
      '<span class="ottoh-done__icon">' + icon('check', 28) + '</span>' +
      '<h2>' + esc(pair[0]) + '</h2><p>' + esc(pair[1]) + '</p>' +
      (steps ? '<ol class="ottoh-done__steps">' + steps.map(function (s) { return '<li>' + esc(s) + '</li>'; }).join('') + '</ol>' : '') +
      '<button type="button" class="ottoh-btn" data-ottoh-done-back>' + esc(TEXT.backToOrder) + '</button>' +
    '</div>';
    var panel = this.result.querySelector('.ottoh-panel');
    if (panel) panel.innerHTML = html;
    this.scrollTo(this.result);

    var back = this.result.querySelector('[data-ottoh-done-back]');
    back.focus({ preventScroll: true });
    back.addEventListener('click', function () {
      self.panel = null;
      self.refresh().then(function () { self.render(); });
    });
  };

  /* ---- small helpers ---------------------------------------------------- */

  Hub.prototype.busy = function (btn, on, label) {
    if (!btn) return;
    if (on) {
      btn.setAttribute('data-label', btn.innerHTML);
      btn.disabled = true;
      btn.classList.add('is-busy');
      if (label) btn.textContent = label;
    } else {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      if (btn.hasAttribute('data-label')) btn.innerHTML = btn.getAttribute('data-label');
    }
  };

  Hub.prototype.copyText = function (el) {
    var value = el.getAttribute('data-value');
    var done = function () {
      el.textContent = TEXT.copied;
      window.setTimeout(function () { el.textContent = TEXT.copy; }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(value).then(done, function () {});
  };

  /* Clears the sticky announcement bar and header Prestige keeps on screen. */
  Hub.prototype.scrollTo = function (el) {
    var rect = el.getBoundingClientRect();
    if (rect.top >= 0 && rect.top < window.innerHeight * 0.4) return;
    var styles = getComputedStyle(document.documentElement);
    var offset = (parseFloat(styles.getPropertyValue('--header-height')) || 0) +
      (parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0) + 16;
    window.scrollTo({ top: window.pageYOffset + rect.top - offset, behavior: 'smooth' });
  };

  /* ---- start ------------------------------------------------------------ */

  function boot(scope) {
    (scope || document).querySelectorAll('[data-ottoh]').forEach(function (root) {
      if (root.hasAttribute('data-ottoh-ready')) return;
      root.setAttribute('data-ottoh-ready', '');
      new Hub(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  // Theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
